import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Crown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBilling } from "@/hooks/useBilling";
import { UsageStats } from "@/components/billing/UsageStats";
import { LifetimeClaimBanner } from "@/components/billing/LifetimeClaimBanner";
import { SubscriptionManage } from "@/components/billing/SubscriptionManage";
import { PlanCard } from "@/components/billing/PlanCard";
import SnapLinkAddon from "@/components/billing/SnapLinkAddon";

export default function Billing() {
  const { currentOrg, currentOrgId, refreshOrgs } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const { plans, offer, busy, polling, offerActive, offerCountdown, claimLifetime, upgrade, manageBilling } =
    useBilling(currentOrgId, refreshOrgs);

  const isOwner = currentOrg?.role === "owner";
  const currentPlan = currentOrg?.plan;
  const isLifetime = !!currentOrg?.lifetime;

  const guard = () => {
    if (!isOwner) { toast.error("Only the workspace owner can change the plan."); return false; }
    if (!agreed) { toast.error("Please agree to the Terms, Privacy Policy and Refund Policy first."); return false; }
    return true;
  };

  const handleUpgrade = (planKey) => { if (guard()) upgrade(planKey); };
  const handleClaim = () => { if (guard()) claimLifetime(); };

  return (
    <AppShell>
      <div className="px-6 py-8 max-w-6xl w-full mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Plans & Billing</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {currentOrg && <>Current plan for <span className="text-zinc-200">{currentOrg.name}</span>: <span className="text-blue-400 font-medium capitalize">{currentPlan}</span></>}
            {!isOwner && <span className="text-zinc-600"> · Only the owner can change plans</span>}
          </p>
        </div>

        {polling && (
          <div className="mb-6 border border-blue-500/30 bg-blue-500/10 rounded-md px-4 py-3 text-sm text-blue-300 font-mono animate-pulse" data-testid="payment-polling">
            Verifying your payment…
          </div>
        )}

        <UsageStats org={currentOrg} />

        {isOwner && <SnapLinkAddon orgId={currentOrgId} isOwner={isOwner} onChanged={refreshOrgs} />}

        {isLifetime && (
          <div data-testid="lifetime-active-notice" className="mb-6 flex items-center gap-2 border border-amber-500/30 bg-amber-500/10 rounded-lg px-5 py-4 text-sm text-amber-300">
            <Crown size={18} weight="fill" className="shrink-0" />
            <div>You own a <span className="font-semibold text-white capitalize">{currentPlan} Lifetime</span> license. No monthly fees, ever. Thank you!</div>
          </div>
        )}

        {offerActive && !isLifetime && isOwner && (
          <LifetimeClaimBanner offer={offer} countdown={offerCountdown} busy={busy} onClaim={handleClaim} />
        )}

        {isOwner && currentPlan && currentPlan !== "personal" && !isLifetime && (
          <SubscriptionManage org={currentOrg} currentPlan={currentPlan} busy={busy} onManage={manageBilling} />
        )}

        {isOwner && !isLifetime && (
          <label className="flex items-start gap-2.5 mb-5 cursor-pointer select-none" data-testid="billing-agree-label">
            <input
              type="checkbox"
              data-testid="billing-agree-checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-black accent-blue-600 cursor-pointer"
            />
            <span className="text-xs text-zinc-400 leading-relaxed">
              I agree to the{" "}
              <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Terms</Link>,{" "}
              <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Privacy Policy</Link>, and{" "}
              <Link to="/refund-policy" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">Refund Policy</Link> (including the 14-day window on lifetime purchases).
            </span>
          </label>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              isCurrent={p.key === currentPlan}
              isOwner={isOwner}
              busy={busy}
              onUpgrade={handleUpgrade}
            />
          ))}
        </div>
        <p className="text-xs text-zinc-600 mt-6 font-mono" data-testid="billing-currency-note">All prices in USD · Payments are processed securely by Stripe.</p>
      </div>
    </AppShell>
  );
}
